import {
  DATE_PRESET_LABEL,
  DATE_PRESETS,
  DEFAULT_LIMIT,
  PAYMENT_TYPES,
  STATUS_FILTERS,
  amountClass,
  appliedChips,
  datePresetRange,
  defaultApplied,
  displayStatus,
  fetchTransactionRecords,
  formatMoney,
  formatTxnDate,
  rowDetails,
  rowKey,
  statusClass,
  toggleListValue,
} from './helpers.js';
import { isAccountAbortError } from '../boot/accountCatalogCache.js';
import { BLOCKING_LOAD_STUCK_MS } from '../boot/playerLoading.js';
import { categoryLabel, createPlayerI18n, phrase } from '../i18n/language.js';

export function createTransactionRecordsPage({ useState, useEffect, useCallback, jsx, jsxs }) {
  const usePlayerI18n = createPlayerI18n({ useState, useEffect });
  function ClipboardEmpty() {
    return jsxs('div', {
      className: 'tr-empty',
      children: [
        jsxs('svg', {
          className: 'tr-empty-icon',
          viewBox: '0 0 64 64',
          'aria-hidden': 'true',
          children: [
            jsx('rect', {
              x: '18',
              y: '12',
              width: '28',
              height: '40',
              rx: '3',
              fill: '#3a3a3a',
              stroke: '#8d8d8d',
              strokeWidth: '2',
            }, 'a'),
            jsx('rect', {
              x: '24',
              y: '8',
              width: '16',
              height: '8',
              rx: '2',
              fill: '#5a5a5a',
              stroke: '#8d8d8d',
              strokeWidth: '2',
            }, 'b'),
            jsx('path', {
              d: 'M26 28h12M26 36h12M26 44h8',
              fill: 'none',
              stroke: '#8d8d8d',
              strokeWidth: '2',
              strokeLinecap: 'round',
            }, 'c'),
          ],
        }),
        jsx('p', { children: phrase('No Data') }),
      ],
    });
  }

  function FunnelIcon() {
    return jsxs('svg', {
      className: 'tr-icon',
      viewBox: '0 0 24 24',
      'aria-hidden': 'true',
      children: [
        jsx('path', {
          d: 'M4 5h16l-6.2 7.4V19l-3.6-2v-4.6L4 5Z',
          fill: 'currentColor',
        }),
      ],
    });
  }

  function Chip({ label, selected, onSelect }) {
    return jsx('button', {
      type: 'button',
      className: selected ? 'tr-chip is-on' : 'tr-chip',
      'aria-pressed': selected,
      onClick: onSelect,
      children: phrase(categoryLabel(label)),
    });
  }

  function TransactionRecordsPage({ onClose }) {
    const { phrase } = usePlayerI18n();
    const [applied, setApplied] = useState(() => defaultApplied());
    const [draft, setDraft] = useState(applied);
    const [filterOpen, setFilterOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expanded, setExpanded] = useState('');
    const [reload, setReload] = useState(0);

    const load = useCallback(async () => {
      const payload = await fetchTransactionRecords(applied, page, DEFAULT_LIMIT);
      return {
        rows: Array.isArray(payload?.records) ? payload.records : [],
        totalPages: Math.max(1, Number(payload?.totalPages) || 1),
      };
    }, [applied, page, reload]);

    useEffect(() => {
      let cancelled = false;
      const stuck = window.setTimeout(() => {
        if (!cancelled) setLoading(false);
      }, BLOCKING_LOAD_STUCK_MS);
      setLoading(true);
      setError('');
      load()
        .then((payload) => {
          if (cancelled) return;
          setRows(payload.rows);
          setTotalPages(payload.totalPages);
        })
        .catch((err) => {
          if (!cancelled && !isAccountAbortError(err)) {
            setError(err.message || phrase('Failed to load'));
            setRows([]);
          }
        })
        .finally(() => {
          window.clearTimeout(stuck);
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
        window.clearTimeout(stuck);
      };
    }, [load]);

    const openFilter = () => {
      setDraft(applied);
      setFilterOpen(true);
    };

    const closeFilterWithoutApply = () => {
      setFilterOpen(false);
    };

    const confirmFilter = () => {
      setApplied(draft);
      setPage(1);
      setExpanded('');
      setFilterOpen(false);
    };

    const closePage = () => {
      if (typeof onClose === 'function') onClose();
    };

    const chips = appliedChips(applied);

    return jsxs('div', {
      className: 'tr-page',
      'aria-label': phrase('Transaction Records'),
      children: [
        jsxs('header', {
          className: 'tr-header',
          children: [
            jsx('h1', { className: 'tr-title', children: phrase('Transaction Records') }),
            jsx('button', {
              type: 'button',
              className: 'tr-close',
              'aria-label': phrase('Close'),
              onClick: closePage,
              children: jsx('svg', {
                className: 'tr-icon',
                viewBox: '0 0 24 24',
                'aria-hidden': 'true',
                children: jsx('path', {
                  d: 'M6 6l12 12M18 6L6 18',
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                }),
              }),
            }),
          ],
        }),
        jsxs('div', {
          className: 'tr-toolbar',
          children: [
            jsx('div', {
              className: 'tr-applied',
              children: chips.map((chip) => jsx('span', {
                className: 'tr-applied-chip',
                children: chip.label,
              }, chip.key)),
            }),
            jsx('button', {
              type: 'button',
              className: 'tr-filter-btn',
              'aria-label': phrase('Transaction Records Filter'),
              onClick: openFilter,
              children: jsx(FunnelIcon, {}),
            }),
          ],
        }),
        jsxs('div', {
          className: 'tr-cols',
          children: [
            jsx('span', { children: phrase('Type') }),
            jsx('span', { className: 'tr-col-amt', children: phrase('Amount') }),
            jsx('span', { children: phrase('Status') }),
            jsx('span', { children: phrase('Txn Date') }),
          ],
        }),
        error
          ? jsxs('p', {
            className: 'tr-error',
            children: [
              error,
              ' ',
              jsx('button', {
                type: 'button',
                onClick: () => setReload((n) => n + 1),
                children: phrase('Retry'),
              }),
            ],
          })
          : null,
        loading
          ? jsx('p', { className: 'tr-loading account-local-status', children: phrase('Loading…') })
          : error
            ? null
          : rows.length
            ? jsx('ul', {
              className: 'tr-list',
              children: rows.map((row) => {
                const key = rowKey(row);
                const open = expanded === key;
                const details = rowDetails(row);
                return jsxs('li', {
                  className: open ? 'tr-row is-open' : 'tr-row',
                  children: [
                    jsxs('button', {
                      type: 'button',
                      className: 'tr-row-main',
                      'aria-expanded': open,
                      onClick: () => setExpanded(open ? '' : key),
                      children: [
                        jsx('span', { className: 'tr-cell', children: phrase(row.type || '—') }),
                        jsx('span', {
                          className: `tr-cell tr-cell-amt ${amountClass(row.amount)}`,
                          children: formatMoney(row.amount),
                        }),
                        jsx('span', {
                          className: 'tr-cell',
                          children: jsx('span', {
                            className: statusClass(row),
                            children: phrase(displayStatus(row)),
                          }),
                        }),
                        jsx('span', {
                          className: 'tr-cell tr-cell-date',
                          children: formatTxnDate(row.txnDate),
                        }),
                        jsx('span', { className: 'tr-chevron', 'aria-hidden': 'true' }),
                      ],
                    }),
                    open && details.length
                      ? jsx('dl', {
                        className: 'tr-details',
                        children: details.map(([label, value]) => jsxs('div', {
                          children: [
                            jsx('dt', { children: label }),
                            jsx('dd', { children: value }),
                          ],
                        }, label)),
                      })
                      : null,
                  ],
                }, key);
              }),
            })
            : jsx(ClipboardEmpty, {}),
        !loading && (page > 1 || page < totalPages)
          ? jsxs('div', {
            className: 'tr-pager',
            children: [
              jsx('button', {
                type: 'button',
                disabled: page <= 1,
                onClick: () => {
                  setPage((n) => Math.max(1, n - 1));
                  setExpanded('');
                },
                children: phrase('Previous'),
              }),
              jsxs('span', { children: [phrase('Page'), ' ', page] }),
              jsx('button', {
                type: 'button',
                disabled: page >= totalPages,
                onClick: () => {
                  setPage((n) => n + 1);
                  setExpanded('');
                },
                children: phrase('Next'),
              }),
            ],
          })
          : null,
        filterOpen
          ? jsxs('div', {
            className: 'tr-sheet',
            role: 'dialog',
            'aria-label': phrase('Transaction Records Filter'),
            children: [
              jsxs('header', {
                className: 'tr-sheet-head',
                children: [
                  jsx('button', {
                    type: 'button',
                    className: 'tr-back',
                    'aria-label': phrase('Back'),
                    onClick: closeFilterWithoutApply,
                    children: '‹',
                  }),
                  jsx('h2', { children: phrase('Transaction Records Filter') }),
                ],
              }),
              jsxs('div', {
                className: 'tr-sheet-body',
                children: [
                  jsx('h3', { children: phrase('Status') }),
                  jsx('div', {
                    className: 'tr-chips',
                    children: STATUS_FILTERS.map((status) => jsx(Chip, {
                      label: status,
                      selected: draft.statuses.includes(status),
                      onSelect: () => setDraft((prev) => ({
                        ...prev,
                        statuses: toggleListValue(prev.statuses, status),
                      })),
                    }, status)),
                  }),
                  jsx('h3', { children: phrase('Payment Type') }),
                  jsx('div', {
                    className: 'tr-chips',
                    children: PAYMENT_TYPES.map((type) => jsx(Chip, {
                      label: type,
                      selected: draft.types.includes(type),
                      onSelect: () => setDraft((prev) => ({
                        ...prev,
                        types: toggleListValue(prev.types, type),
                      })),
                    }, type)),
                  }),
                  jsx('h3', { children: phrase('Date') }),
                  jsx('div', {
                    className: 'tr-chips tr-chips-date',
                    children: DATE_PRESETS.map((key) => jsx(Chip, {
                      label: DATE_PRESET_LABEL[key],
                      selected: draft.preset === key,
                      onSelect: () => setDraft((prev) => ({ ...prev, ...datePresetRange(key) })),
                    }, key)),
                  }),
                ],
              }),
              jsx('button', {
                type: 'button',
                className: 'tr-confirm',
                onClick: confirmFilter,
                children: phrase('Confirm'),
              }),
            ],
          })
          : null,
      ],
    });
  }

  return TransactionRecordsPage;
}
